/* ProductVideoField — SATU video per produk induk (dibagi semua varian).
   Upload langsung ke POST /api/admin/products/{id}/video (produk harus sudah tersimpan). */
import React, { useRef, useState } from "react";
import { Upload, Trash2, RefreshCw, Video, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { imageUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export const PRODUCT_VIDEO_MAX_MB = 30;
const ACCEPT = "video/mp4,.mp4";

export default function ProductVideoField({ productId, value, posterSrc, onChange, disabled }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null); // null | 0..100
  const [error, setError] = useState("");
  const busy = progress !== null;

  const pick = () => inputRef.current?.click();

  const upload = async (file) => {
    if (!file) return;
    setError("");
    if (!/\.mp4$/i.test(file.name)) { setError("Format harus MP4."); return; }
    if (file.size > PRODUCT_VIDEO_MAX_MB * 1024 * 1024) { setError(`Ukuran video melebihi ${PRODUCT_VIDEO_MAX_MB} MB.`); return; }
    if (file.size === 0) { setError("File kosong."); return; }
    const fd = new FormData();
    fd.append("file", file);
    setProgress(0);
    try {
      const r = await api.post(`/admin/products/${productId}/video`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(e.total ? Math.round((e.loaded * 100) / e.total) : 0),
      });
      onChange(r.data?.product_video || null);
      toast.success(value ? "Video produk diganti" : "Video produk ditambahkan");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setProgress(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    setError("");
    setProgress(0);
    try {
      await api.delete(`/admin/products/${productId}/video`);
      onChange(null);
      toast.success("Video produk dihapus");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setProgress(null);
    }
  };

  return (
    <div data-testid="product-video-field">
      <Label>Video Produk</Label>
      <p className="text-xs text-[#5C5C5C] mt-0.5" data-testid="product-video-help">
        Maksimal 1 video per produk · MP4 · maks {PRODUCT_VIDEO_MAX_MB} MB · berlaku untuk semua varian
      </p>
      <input ref={inputRef} type="file" accept={ACCEPT} hidden disabled={disabled || busy}
        onChange={(e) => upload(e.target.files?.[0])} data-testid="product-video-input" />

      {!productId ? (
        <div className="mt-2 text-xs italic text-[#5C5C5C] border border-dashed border-[#E5E5E5] rounded-xl p-3" data-testid="product-video-save-first">
          Simpan produk terlebih dahulu, lalu tambahkan video.
        </div>
      ) : value?.url ? (
        <div className="mt-2 space-y-2">
          <video src={imageUrl(value.url)} poster={posterSrc || undefined} controls playsInline preload="metadata"
            className="w-full max-h-64 rounded-xl bg-black object-contain" data-testid="product-video-preview" />
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" onClick={pick} disabled={disabled || busy} className="rounded-lg" data-testid="product-video-replace">
              <RefreshCw size={14} className="mr-1" /> Ganti Video
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={remove} disabled={disabled || busy} className="rounded-lg text-[#DC2F02]" data-testid="product-video-remove">
              <Trash2 size={14} className="mr-1" /> Hapus Video
            </Button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={pick} disabled={disabled || busy}
          className="mt-2 w-full rounded-xl border-2 border-dashed border-[#E5E5E5] p-4 grid place-items-center gap-1 hover:bg-[#FAF9F6] disabled:opacity-60"
          data-testid="product-video-upload">
          {busy ? <Loader2 size={20} className="animate-spin text-[#5C5C5C]" /> : <Video size={20} className="text-[#5C5C5C]" />}
          <span className="text-xs text-[#5C5C5C] flex items-center gap-1"><Upload size={12} /> Unggah video MP4</span>
        </button>
      )}

      {busy && (
        <div className="mt-2" data-testid="product-video-progress">
          <div className="h-1.5 rounded-full bg-[#E5E5E5] overflow-hidden">
            <div className="h-full bg-[#E85D04] transition-[width]" style={{ width: `${progress}%` }} />
          </div>
          <div className="text-[11px] text-[#5C5C5C] mt-1">Mengunggah… {progress}%</div>
        </div>
      )}
      {error && <div className="text-xs text-[#DC2F02] mt-1" data-testid="product-video-error">{error}</div>}
    </div>
  );
}
